import React from "react";
import { HeaderAction, HeaderComponent } from "../../../types";
import { renderIcon } from "../../../utils/icons";
import VisibilitySettings from "./VisibilitySettings";

interface SearchBarSettingsProps {
	component: HeaderComponent;
	dispatch: React.Dispatch<HeaderAction>;
	onBackClick: () => void;
}

const SearchBarSettings: React.FC<SearchBarSettingsProps> = ({
	component,
	dispatch,
	onBackClick,
}) => {
	const searchIconOptions = [
		{ value: "search", label: "Search" },
		{ value: "grid", label: "Grid" },
		{ value: "settings", label: "Settings" },
		{ value: "mail", label: "Mail" },
	];

	// New search bar design templates
	const searchDesigns = [
		{
			id: "default",
			name: "Default",
			styles: {
				border: "1px solid #e5e7eb",
				borderRadius: "0.375rem",
				padding: "8px 12px",
			},
		},
		{
			id: "minimal",
			name: "Minimal",
			styles: {
				border: "none",
				borderBottom: "1px solid #e5e7eb",
				borderRadius: "0",
				padding: "8px 0",
			},
		},
		{
			id: "rounded",
			name: "Rounded",
			styles: {
				border: "1px solid #e5e7eb",
				borderRadius: "9999px",
				padding: "8px 16px",
			},
		},
		{
			id: "filled",
			name: "Filled",
			styles: {
				border: "none",
				borderRadius: "0.375rem",
				padding: "8px 12px",
				background: "#f3f4f6",
			},
		},
		{
			id: "shadowed",
			name: "Shadowed",
			styles: {
				border: "1px solid #e5e7eb",
				borderRadius: "0.375rem",
				padding: "8px 12px",
				boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
			},
		},
	];

	return (
		<div className="p-4">
			<div className="flex items-center mb-4">
				<button
					className="mr-2 p-1 rounded hover:bg-gray-100"
					onClick={onBackClick}>
					{renderIcon("menu", 16)}
				</button>
				<h3 className="text-lg font-medium">Search Bar Settings</h3>
			</div>

			<div className="mb-4">
				<label className="block text-sm font-medium mb-1">
					Display Mode
				</label>
				<select
					className="w-full p-2 border rounded"
					value={component.settings?.size}
					onChange={(e) =>
						dispatch({
							type: "UPDATE_COMPONENT",
							payload: {
								id: component.id,
								updates: {
									settings: {
										...component.settings,
										size: e.target.value,
									},
								},
							},
						})
					}>
					<option value="full">Full Search Bar</option>
					<option value="icon">Icon Only</option>
				</select>
			</div>

			{/* Search Design Templates */}
			{component.settings?.size === "full" && (
				<div className="mb-4">
					<label className="block text-sm font-medium mb-1">
						Design Template
					</label>
					<div className="grid grid-cols-2 gap-2">
						{searchDesigns.map((design) => (
							<button
								key={design.id}
								className={`p-2 border rounded ${
									component.settings?.design === design.id
										? "border-blue-500 bg-blue-50"
										: "border-gray-300"
								}`}
								onClick={() =>
									dispatch({
										type: "UPDATE_COMPONENT",
										payload: {
											id: component.id,
											updates: {
												settings: {
													...component.settings,
													design: design.id,
												},
												styles: {
													...component.styles,
													...design.styles,
												},
											},
										},
									})
								}>
								<div className="text-sm font-medium mb-1">
									{design.name}
								</div>
								<div
									className="h-8 border w-full flex items-center px-2"
									style={{
										...design.styles,
										background:
											design.styles.background ||
											"#ffffff",
									}}>
									<span className="text-gray-400 mr-1">
										{renderIcon("search", 14)}
									</span>
									<span className="text-xs text-gray-400">
										Search...
									</span>
								</div>
							</button>
						))}
					</div>
				</div>
			)}

			<div className="mb-4">
				<label className="block text-sm font-medium mb-1">Icon</label>
				<div className="grid grid-cols-4 gap-2 mb-2">
					{searchIconOptions.map((option) => (
						<button
							key={option.value}
							className={`p-2 border rounded flex flex-col items-center ${
								component.settings?.icon === option.value
									? "border-blue-500 bg-blue-50"
									: "border-gray-300"
							}`}
							onClick={() =>
								dispatch({
									type: "UPDATE_COMPONENT",
									payload: {
										id: component.id,
										updates: {
											settings: {
												...component.settings,
												icon: option.value,
											},
										},
									},
								})
							}>
							<div className="mb-1">
								{renderIcon(option.value)}
							</div>
							<div className="text-xs">{option.label}</div>
						</button>
					))}
				</div>
			</div>

			<div className="mb-4">
				<label className="block text-sm font-medium mb-1">
					Placeholder Text
				</label>
				<input
					type="text"
					className="w-full p-2 border rounded"
					value={component.settings?.placeholder}
					onChange={(e) =>
						dispatch({
							type: "UPDATE_COMPONENT",
							payload: {
								id: component.id,
								updates: {
									settings: {
										...component.settings,
										placeholder: e.target.value,
									},
								},
							},
						})
					}
				/>
			</div>

			<div className="mb-4">
				<label className="block text-sm font-medium mb-1">Width</label>
				<input
					type="text"
					className="w-full p-2 border rounded"
					value={component.styles?.width}
					onChange={(e) =>
						dispatch({
							type: "UPDATE_COMPONENT",
							payload: {
								id: component.id,
								updates: {
									styles: {
										...component.styles,
										width: e.target.value,
									},
								},
							},
						})
					}
				/>
			</div>

			<VisibilitySettings component={component} dispatch={dispatch} />
		</div>
	);
};

export default SearchBarSettings;
